import React from 'react';
import { MessageSquare, Heart, Brain, Users as Users2, Zap, CheckCircle2 } from 'lucide-react';

interface SoftSkill {
  name: string;
  score: number;
  description?: string;
  impact?: string;
}

interface SoftSkillsAssessmentProps {
  skills: SoftSkill[];
  title?: string;
}

const SoftSkillsAssessment: React.FC<SoftSkillsAssessmentProps> = ({
  skills,
  title = 'Soft Skills Assessment'
}) => {
  const getIcon = (name: string) => {
    const nameLower = name.toLowerCase();
    if (nameLower.includes('communication') || nameLower.includes('presentation')) return MessageSquare;
    if (nameLower.includes('emotional') || nameLower.includes('empathy')) return Heart;
    if (nameLower.includes('problem') || nameLower.includes('critical') || nameLower.includes('analytical')) return Brain;
    if (nameLower.includes('collaboration') || nameLower.includes('teamwork')) return Users2;
    if (nameLower.includes('adaptability') || nameLower.includes('flexibility')) return Zap;
    return CheckCircle2;
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'from-purple-500 to-pink-500';
    if (score >= 70) return 'from-blue-500 to-cyan-500';
    if (score >= 50) return 'from-amber-400 to-orange-500';
    return 'from-gray-400 to-gray-500';
  };

  const getScoreRating = (score: number) => {
    if (score >= 85) return { label: 'Exceptional', color: 'text-purple-600' };
    if (score >= 70) return { label: 'Strong', color: 'text-blue-600' };
    if (score >= 50) return { label: 'Good', color: 'text-amber-600' };
    return { label: 'Developing', color: 'text-gray-600' };
  };

  const averageScore = skills.length > 0
    ? Math.round(skills.reduce((sum, skill) => sum + skill.score, 0) / skills.length)
    : 0;

  const sortedSkills = [...skills].sort((a, b) => b.score - a.score);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-purple-50 via-pink-50 to-blue-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600 mt-1">Interpersonal and transferable skills evaluation</p>
          </div>
          <div className="flex flex-col items-center">
            <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {averageScore}
            </div>
            <div className="text-xs text-gray-600 font-medium">Overall</div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-4">
          {sortedSkills.map((skill, index) => {
            const Icon = getIcon(skill.name);
            const rating = getScoreRating(skill.score);
            const gradientColor = getScoreColor(skill.score);

            return (
              <div
                key={index}
                className="group relative bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 p-3 rounded-lg bg-gradient-to-br ${gradientColor} shadow-md group-hover:scale-110 transition-transform`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>

                  <div className="flex-grow min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900 text-base mb-1">
                          {skill.name}
                        </h4>
                        {skill.description && (
                          <p className="text-sm text-gray-600 mb-2">
                            {skill.description}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end ml-4">
                        <span className={`text-2xl font-bold ${rating.color}`}>
                          {skill.score}
                        </span>
                        <span className={`text-xs font-medium ${rating.color}`}>
                          {rating.label}
                        </span>
                      </div>
                    </div>

                    <div className="relative">
                      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div
                          className={`h-full bg-gradient-to-r ${gradientColor} rounded-full transition-all duration-700 ease-out relative`}
                          style={{ width: `${skill.score}%` }}
                        >
                          <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                        </div>
                      </div>
                      <div className="flex justify-between mt-1 text-xs text-gray-500">
                        <span>0</span>
                        <span>50</span>
                        <span>100</span>
                      </div>
                    </div>

                    {skill.impact && (
                      <div className="mt-3 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                        <span className="text-blue-600 text-xs font-medium flex-shrink-0 mt-0.5">Impact:</span>
                        <p className="text-xs text-gray-700">{skill.impact}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-white border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-sm">85+</span>
            </div>
            <span className="text-xs text-gray-600 font-medium">Exceptional</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-sm">70+</span>
            </div>
            <span className="text-xs text-gray-600 font-medium">Strong</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-sm">50+</span>
            </div>
            <span className="text-xs text-gray-600 font-medium">Good</span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-sm">&lt;50</span>
            </div>
            <span className="text-xs text-gray-600 font-medium">Developing</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SoftSkillsAssessment;
